// istanbul ignore file
import React, { useState, useEffect } from 'react'
import { GenXDropdown, type Option } from '../../../../components/GenXDropdown'
import { type Stage, type Parameter } from '../create-custom-kpi/types'
import { FormHelperText, Grid, TextField, Typography } from '@mui/material'
import LanguageStrings from '../../../../i18n/locales'
import { fetchParameterConfigs, type ParameterConfig } from '../../services/deviceParameterService'
import { getDeviceLevelKpiList } from '../../services/deviceKpiDetailsService'
import { fetchKpiConfigs } from '../../services/deviceKpiParameterService'

const customKpi = LanguageStrings().ThermalIQStrings.CreateCustomKpiComponent
const commonTextStyle = { fontSize: '10px', display: 'block', color: '#F0F0F0', fontFamily: 'Honeywell Sans Web' }

interface ParameterFormProps {
  assetList: any
  isTotalizer: boolean
  index: number
  parameter: Parameter
  fieldKey: keyof Stage
  handleStageChange: (index: number, field: keyof Stage, value: Option | any, asset: Option | null, device: Option | null, constantValue?: string) => void
  kpiType: string
  deviceType: string
  currentKpiId?: string
}

interface LabelValue {
  label: string
  value: string
  typeId?: string
}

const ParameterForm: React.FC<ParameterFormProps> = (props) => {
  const [selectedDevice, setSelectedDevice] = useState<LabelValue | any>(props.parameter.device !== '' && props.parameter.device !== null ? { label: props.parameter.device, value: props.parameter.deviceId } : null)
  const [selectedParameter, setSelectedParameter] = useState<Option | any>(props.parameter.parameter !== '' ? { label: props.parameter.parameter, value: props.parameter.parameterId } : null)
  const [parameterOptions, setParameterOptions] = useState<Option[]>([])
  const [kpiParameterOptions, setKpiParameterOptions] = useState<Option[]>([])
  const [isConstant, setIsConstant] = useState(false)
  const [constantValue, setConstantValue] = useState<any>(props.parameter.constantValue !== undefined ? props.parameter.constantValue : '')
  const [constantErrorInit, setConstantErrorInit] = useState(false)
  const tempAssetVal = '00000000-0000-0000-0000-000000000000'

  useEffect(() => {
    if (props.isTotalizer) {
      setIsConstant(false)
      setConstantValue('')
      if (props.parameter?.parameterType === 'CONSTANT') setSelectedParameter(null)
    }
  }, [props.isTotalizer])

  useEffect(() => {
    setConstantValue('')
    if (props.parameter?.device !== '' && props.parameter?.device !== null) {
      setSelectedDevice({ label: props.parameter.device, value: props.parameter.deviceId })
      setSelectedParameter({ label: props.parameter.parameter, value: props.parameter.parameterId })
    } else if (props.parameter?.constantValue !== undefined && props.parameter?.constantValue !== '') {
      setIsConstant(true)
      setConstantValue(props.parameter.constantValue)
      setSelectedDevice({ label: 'Constant', value: 'Constant', type: 'CONSTANT' })
      setSelectedParameter({ label: 'Constant', value: 'Constant', type: 'CONSTANT' })
    } else if (props.parameter?.device !== '') {
      (props.parameter.parameterId !== '' && props.parameter.parameterId !== null ? setSelectedParameter({ label: props.parameter.parameter, value: props.parameter.parameterId }) : setSelectedParameter(null))
    }
  }, [props.parameter])

  useEffect(() => {
    const fetchConfigs = async (): Promise<void> => {
      if ((selectedDevice !== null && selectedDevice.value !== '' && selectedDevice.value !== 'Constant') || props.kpiType === 'Device Level Custom KPI') {
        try {
          if (props.kpiType === 'Asset Level Custom KPI' && (selectedDevice !== null && selectedDevice.value !== '')) {
            const matchingDevice = props.assetList.find((device: any) => device.id === selectedDevice.value)
            const deviceTypeId = matchingDevice?.deviceTypeId
            setParameterOptions([])
            await fetchParameterConfigs(selectedDevice.value, deviceTypeId, handleSuccess, handleFailure) // api to fetch parameter in both level
            await fetchKpiConfigs(selectedDevice.value, deviceTypeId, handleKpiSuccess, handleFailure)
          } else {
            await fetchParameterConfigs(tempAssetVal, props.deviceType, handleSuccess, handleFailure)
            await getDeviceLevelKpiList(props.deviceType, handleKpiSuccess, handleFailure)
          }
        } catch (error) {
          console.error('Failed to fetch parameters:', error)
        }
      }
    }
    fetchConfigs().catch(error => {
      console.error('Fetch configs failed:', error)
    })
  }, [selectedDevice, props.isTotalizer, props.kpiType])

  const handleSuccess = (configs: ParameterConfig[], httpStatus: number): void => {
    let fetchedParameterOptions
    if (props.kpiType === 'Device Level Custom KPI') {
      fetchedParameterOptions = configs.map(param => ({
        label: `${param.parameterName?.toString() ?? ''}-${param.registerAddress?.toString() ?? ''}`,
        value: param.cimCodeId?.toString() ?? '',
        type: 'REGISTER_ID'
      }))
    } else {
      fetchedParameterOptions = configs
        .filter((param: { isThisFromDefaultConfig: boolean, isRegisterEnabled: boolean }) => !param.isThisFromDefaultConfig && param.isRegisterEnabled)
        .map(param => ({
          label: `${param.parameterName?.toString() ?? ''}-${param.registerAddress?.toString() ?? ''}`,
          value: param.cimCodeId?.toString() ?? '',
          type: 'REGISTER_ID'
        }))
    }
    const deviceParamOptions = props.kpiType === 'Device Level Custom KPI' ? [{ value: 'Constant', label: 'Constant', type: 'CONSTANT' }, ...fetchedParameterOptions] : fetchedParameterOptions
    const finalParameterOptions = props.isTotalizer ? fetchedParameterOptions : props.index === 0 ? [...deviceParamOptions] : [{ value: '', label: 'None' }, ...deviceParamOptions]

    setParameterOptions(finalParameterOptions)
  }

  const handleKpiSuccess = (configs: any, httpStatus: number): void => {
    let fetchedKpiParameterOptions
    if (props.kpiType === 'Device Level Custom KPI') {
      fetchedKpiParameterOptions = configs.content
        .filter((param: { kpi_definition_id: { toString: () => string } }) =>
          // Filter out the current KPI being edited
          param.kpi_definition_id?.toString() !== props.currentKpiId
        )
        .map((param: { kpi_name: { toString: () => string }, kpi_definition_id: { toString: () => string }, is_custom_kpi?: boolean }) => ({
          label: param.kpi_name?.toString() ?? '',
          value: param.kpi_definition_id?.toString() ?? '',
          type: (param.is_custom_kpi === true) ? 'CUSTOM_KPI' : 'STANDARD_KPI'
        }))
    } else {
      fetchedKpiParameterOptions = configs
        .filter((param: { isKpiEnabled: boolean, isCustomKpi: boolean, isDefaultConfig: boolean, kpiDefinitionId: { toString: () => string } }) =>
          param.isKpiEnabled &&
          !param.isDefaultConfig &&
          // Filter out the current KPI being edited
          param.kpiDefinitionId?.toString() !== props.currentKpiId
        )
        .map((param: { kpiName: { toString: () => string }, kpiDefinitionId: { toString: () => string }, isCustomKpi: boolean }) => ({
          label: param.kpiName?.toString() ?? '',
          value: param.kpiDefinitionId?.toString() ?? '',
          type: param.isCustomKpi ? 'CUSTOM_KPI' : 'STANDARD_KPI'
        }))
    }
    setKpiParameterOptions(fetchedKpiParameterOptions)
  }

  const handleFailure = (errorMessage: any, httpStatus: number): void => {
    console.error('Failed to fetch parameters:', errorMessage)
  }

  const handleDeviceChange = (value: LabelValue): void => {
    if (value.value === '' && props.index > 0) {
      setIsConstant(false)
      setSelectedDevice(null)
      setSelectedParameter(null)
      props.handleStageChange(props.index, props.fieldKey, { label: '', value: '' }, null, null, '')
    } else if (value.value === 'Constant') {
      setIsConstant(true)
      setSelectedParameter(null)
      setSelectedDevice(value)
      props.handleStageChange(props.index, props.fieldKey, { label: '', value: '' }, null, null, undefined)
    } else {
      setIsConstant(false)
      setSelectedParameter(null)
      setSelectedDevice(value)
      props.handleStageChange(props.index, props.fieldKey, { label: '', value: '', type: '' }, null, null)
      fetchParameterConfigs(value.value, value.typeId ?? '', handleSuccess, handleFailure).catch(error => {
        console.error('Failed to fetch parameters:', error)
      })
    }
  }

  const handleParameterChange = (value: Option): void => {
    if (value.value === '' && props.index > 0) {
      setIsConstant(false)
      setSelectedParameter(null)
      props.handleStageChange(props.index, props.fieldKey, { label: '', value: '' }, null, null)
    } else if (value.value === 'Constant') {
      setIsConstant(true)
      setSelectedParameter(value)
      setConstantValue('')
      props.handleStageChange(props.index, props.fieldKey, { label: '', value: '', type: 'CONSTANT' }, null, null, undefined)
    } else {
      setIsConstant(false)
      setSelectedParameter(value)
      props.handleStageChange(props.index, props.fieldKey, value, selectedDevice, props.kpiType === 'Device Level Custom KPI' ? null : selectedDevice)
    }
  }

  const fetchedDeviceOptions: LabelValue[] = (props.assetList != null)
    ? (props.assetList).map((device: { name: any, id: any, deviceTypeId: any }) => ({ label: device.name, value: device.id, typeId: device.deviceTypeId }))
    : []
  const deviceOptions = props.isTotalizer ? fetchedDeviceOptions : props.index === 0 ? [{ value: 'Constant', label: 'Constant' }, ...fetchedDeviceOptions] : [{ value: '', label: 'None' }, { value: 'Constant', label: 'Constant' }, ...fetchedDeviceOptions]

  const DEVICE_PLACEHOLDER = props.isTotalizer ? 'Select Device' : 'Select Device or Constant'
  const PARAMETER_PLACEHOLDER = 'Select Parameter'
  const CONST_PARAMETER_PLACEHOLDER = props.isTotalizer ? 'Select Parameter' : 'Select Parameter or Constant'

  const textStyle = {
    root: {
      backgroundColor: '#272727',
      '& .MuiInputBase-input': {
        color: '#F0F0F0',
        backgroundColor: '#404040'
      },
      '& .MuiInputLabel-root': {
        color: '#F0F0F0',
        '&.Mui-focused': {
          color: '#F0F0F0'
        }
      },
      '& .MuiOutlinedInput-root': {
        '& fieldset': {
          borderColor: '#00000000'
        },
        '&:hover fieldset': {
          borderColor: '#00000000'
        },
        '&.Mui-focused fieldset': {
          borderColor: '#00000000'
        }
      }
    }
  }

  const isValidConstant = (value: string): boolean => {
    const regexValidator = /^-?\d{1,500}(\.\d+)?$/
    return regexValidator.test(value)
  }
  const isConstantNotValid = (): boolean => {
    return (!isValidConstant(constantValue))
  }

  const handleConstantChange = (value: string): void => {
    setConstantValue(value)
    setConstantErrorInit(true)
    props.handleStageChange(props.index, props.fieldKey, { label: '', value: '', type: 'CONSTANT' }, null, null, value)
  }

  return (
    <div data-testid="parameter-form-input">
      <Grid container spacing={2}>
        {
          props.kpiType === 'Device Level Custom KPI'
            ? (
                <>
                  <Grid item sx={{ marginTop: 1, marginBottom: 1 }}>
                    <span style={ commonTextStyle }>
                      <Typography sx={{ fontFamily: 'Honeywell Sans Web', fontSize: '14px', letterSpacing: '0.5px' }}>
                        { (props.fieldKey === 'parameter1' && props.isTotalizer) ? 'Condition Parameter to Totalize' : (props.fieldKey === 'parameter2' && props.isTotalizer) ? 'Variable to Totalize' : `${customKpi.Parameter} or ${customKpi.Constant}` }
                        {props.index === 0 ? <sup style={{ color: '#B0B0B0' }}> *</sup> : null}
                      </Typography>
                    </span>
                    <GenXDropdown
                      placeholder={CONST_PARAMETER_PLACEHOLDER}
                      options={[...parameterOptions, ...kpiParameterOptions]}
                      value={selectedParameter}
                      onSelect={handleParameterChange}
                      width={235}
                    />
                  </Grid>
                  {isConstant && (
                    <Grid item>
                      <Typography sx={{ fontFamily: 'Honeywell Sans Web', fontSize: '15px', padding: 0.5, display: 'block' }}>
                        {customKpi.Constant}
                        {props.index === 0
                          ? <sup style={{ color: '#B0B0B0' }}> *</sup>
                          : null
                        }
                      </Typography>
                      <TextField
                        id='remarks-textfield'
                        variant="outlined"
                        placeholder='Enter constant value'
                        InputLabelProps={{
                          disabled: true
                        }}
                        size='small'
                        className='custom-textfield'
                        value={constantValue}
                        onChange={(e) => { handleConstantChange(e.target.value) }}
                        sx={{
                          ...textStyle.root,
                          width: '112%',
                          border: (isConstantNotValid() && constantErrorInit) ? '1px solid #EE3426' : 'none'
                        }}
                      />
                      {(isConstantNotValid() && constantErrorInit) && (
                        <FormHelperText sx={{ color: '#EE3426', paddingLeft: '10px', whiteSpace: 'nowrap' }} className="errorColor">
                          {customKpi.ConstantValidationMessage}
                        </FormHelperText>
                      )}
                    </Grid>
                  )}
                </>
              )
            : (
                <>
                  <Grid item>
                    <span style={ commonTextStyle }>
                      <Typography sx={{ fontFamily: 'Honeywell Sans Web', fontSize: '14px', letterSpacing: '0.5px' }}>
                        { (props.isTotalizer) ? 'Asset or Device' : `Asset or ${customKpi.Device} or ${customKpi.Constant}` }
                        {props.index === 0 ? <sup style={{ color: '#B0B0B0' }}> *</sup> : null}
                      </Typography>
                    </span>
                    <GenXDropdown
                      placeholder={DEVICE_PLACEHOLDER}
                      options={deviceOptions}
                      value={selectedDevice}
                      onSelect={handleDeviceChange}
                      width={235}
                    />
                  </Grid>
                  {!isConstant
                    ? (
                        <Grid item>
                          <span style={ commonTextStyle }>
                            <Typography sx={{ fontFamily: 'Honeywell Sans Web', fontSize: '14px', letterSpacing: '0.5px' }}>
                              { (props.fieldKey === 'parameter1' && props.isTotalizer) ? 'Condition Parameter to Totalize' : (props.fieldKey === 'parameter2' && props.isTotalizer) ? 'Variable to Totalize' : props.fieldKey === 'parameter1' ? 'Parameter 1' : 'Parameter 2' }
                              {props.index === 0 ? <sup style={{ color: '#B0B0B0' }}> *</sup> : null}
                            </Typography>
                          </span>
                          <GenXDropdown
                            placeholder={PARAMETER_PLACEHOLDER}
                            options={[...parameterOptions, ...kpiParameterOptions]}
                            value={selectedParameter}
                            onSelect={handleParameterChange}
                            width={235}
                          />
                        </Grid>
                      )
                    : (
                        <Grid item>
                          <Typography sx={{ fontFamily: 'Honeywell Sans Web', fontSize: '15px', padding: 0.5, display: 'block' }}>
                            {customKpi.Constant}
                            {props.index === 0
                              ? <sup style={{ color: '#B0B0B0' }}> *</sup>
                              : null
                            }
                          </Typography>
                          <TextField
                            id='remarks-textfield'
                            variant="outlined"
                            placeholder='Enter constant value'
                            InputLabelProps={{
                              disabled: true
                            }}
                            size='small'
                            className='custom-textfield'
                            value={constantValue}
                            onChange={(e) => { handleConstantChange(e.target.value) }}
                            sx={{
                              ...textStyle.root,
                              width: '112%',
                              border: (isConstantNotValid() && constantErrorInit) ? '1px solid #EE3426' : 'none'
                            }}
                          />
                          {(isConstantNotValid() && constantErrorInit) && (
                            <FormHelperText sx={{ color: '#EE3426', paddingLeft: '10px', whiteSpace: 'nowrap' }} className="errorColor">
                              {customKpi.ConstantValidationMessage}
                            </FormHelperText>
                          )}
                        </Grid>
                      )
                  }
                </>
              )
        }
      </Grid>
    </div>
  )
}

export default ParameterForm
